import React, { useState, forwardRef, useEffect, useRef, useMemo } from "react";
import FetchData, { CreateEntry, UpdateEntry, GetList, GetOne } from '../../providers/dataProvider';
import { CheckNull, calculateNetPrice } from "../../utils/Validate";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import UnSaveModel from "../../components/common/UnSaveModel";
import urls from "../../constants";
import { Configuration } from "../../components/quotation/Configurations";
import { hasListPriceRoute, useListPriceCalculation } from "../../hooks/useListPriceCalculation";
import { useMultiplier } from "../../hooks/useMultiplier";
import { PriceRecalculate } from "./PriceRecalculate";
import { ProductImportFile } from "./ProductImportFile";
import Loader from "../common/Loader";
import { useVariantOptions } from "../../components/quotation/SizeSelectField";
import { ProductPreviewPanel } from "../../components/quotation/ProductPreviewPanel.js";
import { ProductMarkWorkspaceHeader } from "./ProductMarkWorkspaceHeader";
import { ProductSizeGrid, isVariantHidden } from "./ProductSizeGrid";
import {
    getColorAreaGroups,
    normaliseColorCode,
    synchroniseQuotationColorCharges,
} from "../../utils/colorCharges";
import "../../assets/styles/assembly-configure.css";

const COLUMN_VISIBILITY_STORAGE_KEY = "pathfinder.productMark.columnVisibility.v2";

const matchesVariantId = (constant, variantId) =>
    Array.isArray(constant)
        ? constant.includes(variantId)
        : constant !== undefined && constant !== null && constant === variantId;

const CFSD_TRANSITION_DEFAULT = "None";
const isCfsdTransitionVariant = (variantId) =>
    matchesVariantId(urls?.CFSD_TRANSITION_TYPE, variantId);

const isCfsdFamilyGroup = (groupId) =>
    groupId === urls?.COMBINATION_FIRE_SMOKE_GROUP_ID ||
    groupId === urls?.SMOKE_DAMPER_GROUP_ID;

const transferStaleColorCharge = (quotation = {}, requestedTargetColor = "") => {
    const currentColors = Array.from(new Set(
        getColorAreaGroups(quotation)
            .map((group) => normaliseColorCode(group?.colorCode))
            .filter(Boolean)
    ));
    const charges = Array.isArray(quotation?.colorCharges) ? quotation.colorCharges : [];
    const staleCharges = charges.filter((charge) => !currentColors.includes(
        normaliseColorCode(charge?.colorCode ?? charge?.ralColorCode)
    ));

    const requestedTarget = normaliseColorCode(requestedTargetColor);
    const targetColor = currentColors.includes(requestedTarget)
        ? requestedTarget
        : currentColors.length === 1
            ? currentColors[0]
            : "";
    if (!targetColor || staleCharges.length === 0) return quotation;

    const transferredAmount = staleCharges.reduce(
        (total, charge) => total + (Number(charge?.chargeAmount ?? charge?.amount) || 0),
        0
    );
    if (!transferredAmount) return quotation;

    let targetFound = false;
    const colorCharges = charges
        .filter((charge) => !staleCharges.includes(charge))
        .map((charge) => {
            const colorCode = normaliseColorCode(charge?.colorCode ?? charge?.ralColorCode);
            if (colorCode !== targetColor) return { ...charge, colorCode };

            targetFound = true;
            return {
                ...charge,
                colorCode,
                chargeAmount: (Number(charge?.chargeAmount ?? charge?.amount) || 0) + transferredAmount,
            };
        });

    if (!targetFound) colorCharges.push({ colorCode: targetColor, chargeAmount: transferredAmount });

    return { ...quotation, colorCharges };
};

const getColumnVisibilityStorageKey = (userId, productTab, quotationId) => {
    const productGroupId = productTab?.id?.productGroupId ?? "no-group";
    const productSubGroupId = productTab?.id?.productSubGroupId ?? "no-sub-group";
    const productId = productTab?.id?.id ?? "no-product";

    return [
        COLUMN_VISIBILITY_STORAGE_KEY,
        userId || "anonymous",
        quotationId || "no-quotation",
        productGroupId,
        productSubGroupId,
        productId,
    ].join(":");
};

const readStoredVisibleColumns = (storageKey) => {
    try {
        const rawValue = localStorage.getItem(storageKey);
        if (!rawValue) return null;

        const parsedValue = JSON.parse(rawValue);
        if (!parsedValue || !Array.isArray(parsedValue.columns)) return null;

        return parsedValue;
    } catch (error) {
        return null;
    }
};

const writeStoredVisibleColumns = (storageKey, payload) => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
    }
};

const WORKSPACE_SNAPSHOT_PRICE_VARIANT_IDS = new Set([
    ...(urls?.LIST_PRICE_CONSTANT || []),
    ...(urls?.NET_LIST_PRICE_CONSTANT || []),
    ...(urls?.UNIT_PRICE_CONSTANT || []),
    ...(urls?.NET_UNIT_PRICE_CONSTANT || []),
]);

// A quotation mark is historical data. Its saved variants must remain visible
// even if the product/subgroup variant setup changes later.
const mergeSavedVariantColumns = (configuredVariants = [], savedVariants = [], allProductVariants = []) => {
    const columns = new Map();
    const keyFor = (variantId) => String(variantId);

    configuredVariants.forEach((variant) => {
        if (variant?.variantId !== null && variant?.variantId !== undefined) {
            columns.set(keyFor(variant.variantId), variant);
        }
    });

    savedVariants.forEach((savedVariant) => {
        if (savedVariant?.variantId === null || savedVariant?.variantId === undefined) return;

        const key = keyFor(savedVariant.variantId);
        const existing = columns.get(key);
        const definition = allProductVariants.find(
            (variant) => String(variant?.variantId) === key
        );

        columns.set(key, {
            ...definition,
            ...existing,
            variantId: savedVariant.variantId,
            variantName: existing?.variantName || definition?.variantName || savedVariant?.variantName || `Variant ${savedVariant.variantId}`,
            inputByUser: existing?.inputByUser ?? definition?.inputByUser ?? savedVariant?.inputByUser ?? false,
        });
    });

    return Array.from(columns.values());
};

const toSnapshotValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value);
};

const buildWorkspaceSnapshot = (productConfig, configSetting, addProduct, variantsArray) => {
    const productName = productConfig?.productName || {};

    const configurationSnapshot = (configSetting || [])
        .map((item) => ({
            attributeId: item?.attributeId ?? "",
            valueName: toSnapshotValue(item?.valueName),
            specialValue: toSnapshotValue(item?.specialValue),
        }))
        .sort((left, right) => Number(left.attributeId) - Number(right.attributeId));

    const rowSnapshot = (addProduct || []).map((row) => ({
        sid: row?.sid ?? "",
        id: row?.id ?? "",
        quoteProductId: row?.quoteProductId ?? "",
        notes: toSnapshotValue(row?.notes),
        specialDesignRequest: toSnapshotValue(row?.specialDesignRequest),
        sdrCharges: toSnapshotValue(row?.sdrCharges),
        costingToken: toSnapshotValue(row?.costingToken),
        margin: toSnapshotValue(row?.margin),
    }));

    const variantSnapshot = (variantsArray || [])
        .filter((variant) => !WORKSPACE_SNAPSHOT_PRICE_VARIANT_IDS.has(variant?.variantId))
        .map((variant) => ({
            sid: variant?.sid ?? "",
            id: variant?.id ?? "",
            quoteProductItemId: variant?.quoteProductItemId ?? "",
            variantId: variant?.variantId ?? "",
            variantValue: toSnapshotValue(variant?.variantValue),
            inputByUser: Boolean(variant?.inputByUser),
        }));

    return JSON.stringify({
        productId: productName?.productId ?? "",
        productGroupId: productName?.productGroupId ?? "",
        productSubGroupId: productName?.productSubGroupId ?? "",
        configurationSnapshot,
        rowSnapshot,
        variantSnapshot,
    });
};

export const ProductQuotation = ({
    setProductTab,
    ProductTab,
    setProductConfig,
    productConfig,
    productSubmit,
    setProductSubmit,
    values,
    setValues,
    attributes,
    setAttributes,
    configSetting,
    setConfigSetting,
    SaveProductMarkAdd,
    isRevisionQuotation,
    onSaveRevisionDraft,
    onCompleteRevision,
    revisionCompleting,
    currencyDetail,
    companyBaseCurrency
}) => {
    const [productVariant, setProductVariant] = useState([]);
    const [sdrOpen, setSDROpen] = useState("hide")
    const [actionMenuOpen, setActionMenuOpen] = useState({})
    const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0 })
    const [variantsArray, setvariantsarray] = useState([])
    const [editMarkDetails, setEditMarkDetails] = useState({})
    const [markCreateMessage, setMarkCreateMessage] = useState(null);
    const [productName, setProductName] = useState({});
    const [createMsg, setCreateMsg] = useState(null)
    const [quoteMsg, setQuoteMsg] = useState(null)
    const [quoteDetails, setQuoteDetails] = useState(null)
    const [productValues, setProductValues] = useState(false)
    const [successshow, setSuccessShow] = useState(false)
    const [modalShow, setModalShow] = useState(false);
    const [recalculateModalShow, setRecalculateModalShow] = useState(false);
    const [recalculateLoading, setRecalculateLoading] = useState(false);
    const [recalculate, setRecalculate] = useState(false)
    const [editConfig, setEditConfig] = useState({})
    const [addProduct, setAddProduct] = useState([])
    const [selectedSizeRowSids, setSelectedSizeRowSids] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [marginRangeError, setMarginRangeError] = useState([]);

    const [invalidUnitPrice, setInvalidUnitPrice] = useState([]);
    const [requiredFieldError, setRequiredFieldError] = useState([]);
    const [productMarkDetails, setProductMarkDetails] = useState(null);
    const [salesMultiplier, setSalesMultiplier] = useState({});
    const [productConfigVariant, setProductConfigVariant] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [pendingColumns, setPendingColumns] = useState([]);
    const [attributeDetails, setattributeDetails] = useState([]);
    const [attributeArray, setAttributeArray] = useState([]);
    const [attributeValueArray, setAttributeValueArray] = useState([]);
    const [groupArray, setGroupArray] = useState([]);
    const [lastKey, setLastKey] = useState("");
    const [endcaplist, setEndCapList] = useState(
        [{ label: "None", value: "None" },
        { label: "One Side", value: "One Side" },
        { label: "Both Sides", value: "Both Sides" }])

    const [deleteProduct, setDeleteProduct] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingSuccessModal, setPendingSuccessModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(false);
    const [lastMatchedProductId, setLastMatchedProductId] = useState(null);
    const [isEditMarkDetailsLoading, setIsEditMarkDetailsLoading] = useState(false);
    const configurationRef = useRef(null);
    const workspaceSnapshotRef = useRef("");
    const workspaceSnapshotReadyRef = useRef(false);
    const lastConfigurationSignatureRef = useRef("");
    const columnDefaultsSignatureRef = useRef("");
    const addProductRef = useRef([]);
    const pendingSizeFocusRef = useRef(null);
    const pendingSizeFocusFrameRef = useRef(null);
    const cfsdPricedQtyRef = useRef({});
    const pendingTokenRefreshRef = useRef(false);
    const persistedColorChargeSignatureRef = useRef("");
    const pendingColorChargeTargetRef = useRef("");
    const [actuatorRefreshSid, setActuatorRefreshSid] = useState(null);
    const [actuatorOverrideRequest, setActuatorOverrideRequest] = useState(null);
    const maxBlankSizeRows = 1;
    const currentUserId = localStorage.getItem("authidentity");

    const columnVisibilityQuotationId = values?.id ?? ProductTab?.id?.item?.quotationId;
    const columnVisibilityStorageKey = getColumnVisibilityStorageKey(
        currentUserId,
        ProductTab,
        columnVisibilityQuotationId
    );


    const assemblyTabs = ProductTab?.assemblyTabs ?? [];


    const { data: faiGroups } = FetchData("productsubgroups", {
        groupId: ProductTab?.productSubCategoryId,
        skip: assemblyTabs.length === 0,
    });

    const faiFromApi = useMemo(
        () => (Array.isArray(faiGroups) ? faiGroups : [])
            .flatMap((g) => g.products ?? [])
            .find((p) => p.isActive !== false),
        [faiGroups]
    );
    // Assembly: the first ProductTab.id is the FAI product (tab clicks replace it later)

    const activeAssemblyKey = ProductTab?.activeAssemblyKey ?? assemblyTabs[0]?.key;

    // Accessories tab: show only the Accessories group (hide Model Config)
    const activeAssemblyTab = assemblyTabs.find((t) => t.key === activeAssemblyKey);
    const isAccessoriesTab = !!activeAssemblyTab?.isAccessories;

    //(FAL245 / KSTL / JB11) la Accessories group hide
    const configGroupFilter =
        assemblyTabs.length > 0
            ? (group) => {
                const isAccessoriesGroup =
                    String(group?.groupName ?? "").trim().toLowerCase() === "accessories";
                return isAccessoriesTab ? isAccessoriesGroup : !isAccessoriesGroup;
            }
            : null;

    // Accessories tab-la "FAI", mathadhula FAL245 / KSTL / JB11
    const accessoriesTab = assemblyTabs.find((t) => t.isAccessories);
    const selectedModel = assemblyTabs.length > 0
        ? (accessoriesTab?.title || "FAI")
        : (productConfig?.productName?.productCode ||
            productConfig?.productName?.productName ||
            "");
    // Right panel-ku FAI summary: assembly selections + accessories values
    const faiSummaryItems = useMemo(() => {
        if (!isAccessoriesTab) return null;
        const accessorySelections = (configSetting ?? [])
            .filter((c) => c?.groupName === "Accessories" && c?.attributeName && c?.valueName)
            .map((c) => ({ label: c.attributeName, value: c.valueName }));
        return [...(activeAssemblyTab?.summary ?? []), ...accessorySelections];
    }, [isAccessoriesTab, activeAssemblyTab, configSetting]);

    const handleAssemblyTabClick = (tab) => {
        if (!tab?.product || tab.key === activeAssemblyKey) return;
        const allSelected =
            ProductTab?.allSelectedConfiguration ?? ProductTab?.selectedConfiguration ?? [];
        const attrNames = new Set(
            (tab.product.productSpecGroupMaps ?? []).flatMap((group) =>
                (group.productSpecMaps ?? []).map((spec) => spec.attributeName)
            )
        );

        

        // clear only the config data of the previous model
        setattributeDetails([]);
        setAttributeArray([]);
        setAttributeValueArray([]);
        setGroupArray([]);
        setProductName({});

        const carried = (configSetting ?? [])
            .filter((c) => c?.attributeName && (c?.valueName || c?.specialValue))
            .map((c) => ({
                attributeName: c.attributeName,
                valueName: c.valueName,
                specialValue: c.specialValue,
            }));
        const carriedNames = new Set(carried.map((c) => c.attributeName));
        const mergedSelected = [
            ...allSelected.filter((c) => !carriedNames.has(c.attributeName)),
            ...carried,
        ];

        setProductTab({
            ...ProductTab,
            id: tab.product,
            selectedConfiguration: mergedSelected.filter((c) => attrNames.has(c.attributeName)),
            allSelectedConfiguration: mergedSelected,
            activeAssemblyKey: tab.key,
        });
    };

    const assemblyTabsNode =
        assemblyTabs.length > 1 ? (
            <div className="asm-model-tabs" role="tablist">
                {assemblyTabs.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={t.key === activeAssemblyKey}
                        className={`asm-model-tab${t.key === activeAssemblyKey ? " asm-model-tab--active" : ""}`}
                        onClick={() => handleAssemblyTabClick(t)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
        ) : null;

    useEffect(() => {
        addProductRef.current = addProduct;
    }, [addProduct]);

    useEffect(() => {
        return () => {
            if (pendingSizeFocusFrameRef.current) {
                cancelAnimationFrame(pendingSizeFocusFrameRef.current);
            }
        };
    }, []);

    useEffect(() => {
        workspaceSnapshotRef.current = "";
        workspaceSnapshotReadyRef.current = false;
    }, [
        ProductTab?.status,
        ProductTab?.id?.id,
        ProductTab?.id?.item?.id,
        ProductTab?.redirectTab,
        ProductTab?.title,
    ]);

    const markWorkspaceSnapshot = () => {
        workspaceSnapshotRef.current = buildWorkspaceSnapshot(
            productConfig,
            configSetting,
            addProduct,
            variantsArray
        );
        workspaceSnapshotReadyRef.current = true;
    };

    useEffect(() => {
        if (workspaceSnapshotReadyRef.current) return;
        if (isEditMarkDetailsLoading) return;
        if (!productConfig?.productName?.productId && !configSetting?.length && !addProduct?.length && !variantsArray?.length) {
            return;
        }

        markWorkspaceSnapshot();
    }, [
        isEditMarkDetailsLoading,
        productConfig?.productName?.productId,
        configSetting,
        addProduct,
        variantsArray,
    ]);
    const AddNewEntry = () => {
        if (ValidEntry() === "error") return;
        AddProduct();
    }

    const { userMultiplier } = useMultiplier();
    const { HandleProductOnChange: HandleProductOnChangeApi, pendingPriceRequestsRef } = useListPriceCalculation({ variantsArray, setvariantsarray, setSalesMultiplier, AddNewEntry, addProduct, setAddProduct, productConfig, configSetting, setErrorMessage, userMultiplier, lastKey, setLastKey, setHasUnsavedChanges })

    const HandleProductOnChange = (...args) => {
        if (assemblyTabs.length > 0) return;
        return HandleProductOnChangeApi(...args);
    };
    const { data: productVariants } = FetchData("productsvariants", { id: ProductTab?.id?.productGroupId });
    const { optionsMap, combinations, loading: optionsLoading } = useVariantOptions(productConfig?.productName?.productId);

    const hasMeaningfulValue = (value) => {
        if (value === null || value === undefined) return false;
        return String(value).trim() !== "";
    };

    const isBlankSizeRow = (row, rowVariants = []) => {
        if (!row) return false;

        const rowFields = [
            row?.notes,
            row?.specialDesignRequest,
            row?.costingToken,
        ];

        if (rowFields.some(hasMeaningfulValue)) return false;
        return !rowVariants.some((variant) => {
            if (!hasMeaningfulValue(variant?.variantValue)) return false;

            // Default "None" Transition Type 
            if (
                isCfsdTransitionVariant(variant?.variantId) &&
                String(variant?.variantValue).trim() === CFSD_TRANSITION_DEFAULT
            ) {
                return false;
            }

            // A generated price of 0 must not turn an otherwise empty
            // trailing work row into a saved product item. Manual Unit Price
            // is the exception: a positive user-entered price is meaningful.
            if (urls?.PRICE_CONSTANT?.includes(variant?.variantId)) {
                return urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId) &&
                    Number(variant?.variantValue) > 0;
            }

            return true;
        });
    };

    const pruneExcessBlankRows = () => {
        const rows = addProductRef.current || [];
        if (rows.length <= maxBlankSizeRows) return;

        const blankRows = rows.filter((row) => {
            const rowVariants = variantsArray.filter((variant) => variant?.sid === row?.sid);
            return isBlankSizeRow(row, rowVariants);
        });

        if (blankRows.length <= maxBlankSizeRows) return;

        const rowsToRemove = blankRows
            .slice(0, blankRows.length - maxBlankSizeRows)
            .map((row) => row?.sid)
            .filter((sid) => sid !== undefined && sid !== null);

        if (rowsToRemove.length === 0) return;

        const sidSet = new Set(rowsToRemove);
        setAddProduct((prev) => prev.filter((row) => !sidSet.has(row?.sid)));
        setvariantsarray((prev) => prev.filter((item) => !sidSet.has(item?.sid)));
        setSelectedSizeRowSids((prev) => prev.filter((sid) => !sidSet.has(sid)));
    };

    const removeBlankRowsForSave = () => {
        const blankRowSids = new Set((addProductRef.current || [])
            .filter((row) => isBlankSizeRow(
                row,
                variantsArray.filter((variant) => variant?.sid === row?.sid)
            ))
            .map((row) => row?.sid));

        if (!blankRowSids.size) return;

        setAddProduct((previous) => previous.filter((row) => !blankRowSids.has(row?.sid)));
        setvariantsarray((previous) => previous.filter((variant) => !blankRowSids.has(variant?.sid)));
        setSelectedSizeRowSids((previous) => previous.filter((sid) => !blankRowSids.has(sid)));
    };

    const HandleOnChangeMargin = (e, productitem) => {
        const value = e.target.value;

        if (value !== "" && /^-?\d*\.?\d*$/.test(value) === false) return;

        // Validation: margin allowed range is -100 to 99.99
        if (value !== "" && value !== "-") {
            const numValue = Number(value);
            if (numValue < -100 || numValue > 99.99) {
                setMarginRangeError((prev) => {
                    const filtered = prev.filter((err) => err?.sid !== productitem?.sid);
                    return [...filtered, { sid: productitem?.sid, message: "Valid margin -100 to 99.99." }];
                });
                return;
            }
        }

        setMarginRangeError((prev) => prev.filter((err) => err?.sid !== productitem?.sid));

        const index = addProduct.findIndex((i) => i.sid === productitem.sid);
        if (index === -1) return;

        setAddProduct((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], margin: value };
            return updated;
        });
        setHasUnsavedChanges(true);
        // Refresh token after margin change (debounced), for new and edit marks.
        if (value !== "" && value !== "-" && !value.endsWith(".")) {
            isPriceRefreshPendingRef.current = true;
            clearTimeout(marginRefreshTimerRef.current);
            marginRefreshTimerRef.current = setTimeout(() => {
                setMarginRefreshSid(productitem.sid);
            }, 600);
        } else {
            clearTimeout(marginRefreshTimerRef.current);
            isPriceRefreshPendingRef.current = false;
        }

        const marginValue =
            value === "" || value === "-" || isNaN(Number(value)) ? 0 : Number(value);

        const rowVariants = variantsArray.filter((v) => v.sid === productitem.sid);
        const quantityItem = rowVariants.find((v) => urls?.QUANTITY_CONSTANT?.includes(v.variantId));
        const listPriceItem = rowVariants.find((v) => urls?.LIST_PRICE_CONSTANT?.includes(v.variantId));
        const unitPriceItem = rowVariants.find((v) => urls?.UNIT_PRICE_CONSTANT?.includes(v.variantId));
        const actuatorCostItem = rowVariants.find((v) => urls?.ACTUATOR_COST_CONSTANT?.includes(v.variantId));

        const listPriceVal = Number(listPriceItem?.variantValue) || 0;
        const unitPriceVal = Number(unitPriceItem?.variantValue) || 0;
        const actuatorCostVal = Number(actuatorCostItem?.variantValue) || 0;
        const quantity = Number(quantityItem?.variantValue) || 1;

        // CFSD: the hook recalculates Unit Price and Total after the margin refresh
        // if (productConfig?.productName?.productGroupId === urls?.COMBINATION_FIRE_SMOKE_GROUP_ID) return;

        let basePrice = unitPriceVal;
        if (basePrice <= 0) basePrice = listPriceVal + actuatorCostVal;
        if (basePrice <= 0) return;

        // CFSD: row margin applies to damper only. Actuator already has its own margin.
        const isCFSD = isCfsdFamilyGroup(productConfig?.productName?.productGroupId);

        const hasDamperSplit = isCFSD && listPriceVal > 0 && unitPriceVal >= listPriceVal;

        const damperBase = hasDamperSplit ? listPriceVal : basePrice;
        const actuatorPart = hasDamperSplit ? unitPriceVal - listPriceVal : 0;

        const damperWithMargin = Number(
            calculateNetPrice({ listPrice: damperBase, margin: marginValue })
        );
        const newTotal = (damperWithMargin + actuatorPart) * quantity;

        const nextTotalPrice = Number.isFinite(newTotal) ? newTotal.toFixed(2) : "0.00";
        const nextListPrice = Number.isFinite(basePrice) ? basePrice.toFixed(2) : "0.00";

        setvariantsarray((prev) =>
            prev.map((variant) =>
                variant.sid === productitem.sid && urls?.NET_LIST_PRICE_CONSTANT?.includes(variant.variantId)
                    ? { ...variant, variantValue: nextListPrice }
                    : variant.sid === productitem.sid && urls?.NET_UNIT_PRICE_CONSTANT?.includes(variant.variantId)
                        ? { ...variant, variantValue: nextTotalPrice }
                        : variant
            )
        );
    };

    const HandleActuatorOverride = ({ product, modelVariant, costVariant, model, cost }) => {
        if (!product || !modelVariant || !model) return;

        const newCost = Number(cost);
        const hasCost = cost !== undefined && cost !== null && Number.isFinite(newCost);

        // Model (and cost for instant UI) set pannu. Final cost/unit/total API-la irundhu varum.
        setvariantsarray((prev) =>
            prev.map((v) => {
                if (v.sid !== product.sid) return v;
                if (v.variantId === modelVariant.variantId) {
                    return { ...v, variantValue: model };
                }
                if (hasCost && costVariant && v.variantId === costVariant.variantId) {
                    return { ...v, variantValue: newCost.toFixed(2) };
                }
                return v;
            })
        );

        setAddProduct((prev) =>
            prev.map((row) => (row.sid === product.sid
                ? { ...row, actuatorModel: model, actuatorOverride: true, actuatorOverrideCost: hasCost ? newCost : undefined }
                : row))
        );
        setHasUnsavedChanges(true);

        setActuatorOverrideRequest({ sid: product.sid, model });
    };

    const clearRowActuatorOverride = (sid) =>
        setAddProduct((prev) =>
            prev.map((row) =>
                row.sid === sid
                    ? { ...row, actuatorOverride: false, actuatorModel: "", actuatorOverrideCost: undefined }
                    : row
            )
        );

    useEffect(() => {
        if (productVariants) {
            if (productVariants?.length > 0 && productVariant?.length <= 0) {

                const subGroupId = ProductTab?.status === "editmark"
                    ? editMarkDetails?.data?.[0]?.productSubGroupId
                    : ProductTab?.id?.productSubGroupId

                if (!subGroupId && ProductTab?.status === "editmark") return;
                const checkGroupVariant = productVariants?.filter((item) => item?.productSubGroupId === subGroupId && item?.isActive === true)
                setProductVariant(checkGroupVariant?.length > 0 ? checkGroupVariant : productVariants?.filter((item) => item?.isActive === true && (item?.productSubGroupId === 0 || item?.productSubGroupId === null)))
                setProductConfigVariant(checkGroupVariant?.length > 0 ? checkGroupVariant : productVariants?.filter((item) => item?.isActive === true && (item?.productSubGroupId === 0 || item?.productSubGroupId === null)))
            }
        }
    }, [productVariants, editMarkDetails, productVariant?.length])

    useEffect(() => {
        setConfigSetting([]);
        setProductConfig({});
        setEditConfig({});
        setLastMatchedProductId(null);
        if (ProductTab?.status === "editmark") {
            setIsEditMarkDetailsLoading(true);
            GetList("productmark", { id: ProductTab?.id?.item?.id }, (response) => {
                setEditMarkDetails(response);
            })
            return;
        }

        setEditMarkDetails({});
        setIsEditMarkDetailsLoading(false);
    }, [ProductTab])


    useEffect(() => {
        if (editMarkDetails && productVariant.length > 0 && attributeArray?.length > 0) {
            let addProductArray = [];
            let productVariantsArray = [];
            if (editMarkDetails?.data?.length > 0 && configSetting?.length <= 0 && !productConfig?.productName?.productName) {
                const savedVariants = editMarkDetails.data[0]?.productItems
                    ?.flatMap((item) => item?.productItemVariants || []) || [];
                const savedVariantColumns = mergeSavedVariantColumns(
                    productVariant,
                    savedVariants,
                    productVariants || []
                );

                // Older saves could include the UI's trailing blank work row.
                // Do not show it while editing, and allow the next save to
                // remove it from the persisted product mark.
                const savedProductItems = (editMarkDetails?.data[0]?.productItems || [])
                    .filter((prodItem) => (prodItem?.productItemVariants || []).some((variant) =>
                        (
                            !urls?.PRICE_CONSTANT?.includes(variant?.variantId) ||
                            (urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId) && Number(variant?.variantValue) > 0)
                        ) && CheckNull(variant?.variantValue) !== ""
                    ));

                savedProductItems.map((prodItem, prodIndex) => {
                    // Saved actuator selection (variants). Used on reopen to keep an override.
                    const savedVariantText = (ids) => {
                        const raw = prodItem?.productItemVariants?.find(
                            (v) => ids?.includes(v?.variantId)
                        )?.variantValue;
                        return raw === null || raw === undefined
                            ? ""
                            : String(raw).replace(/,/g, "").trim();
                    };
                    const savedActuatorModelText = savedVariantText(urls?.ACTUATOR_MODEL_CONSTANT);
                    const savedActuatorCostText = savedVariantText(urls?.ACTUATOR_COST_CONSTANT);

                    addProductArray.push({
                        sid: (prodIndex * 1) + 1,
                        id: prodItem?.id,
                        quoteProductId: prodItem?.quoteProductId,
                        notes: prodItem?.notes,
                        specialDesignRequest: prodItem?.specialDesignRequest,
                        sdrCharges: prodItem?.sdrCharges,
                        costingToken: prodItem?.costingToken,
                        powderArea: prodItem?.powderAreaPerUnit ?? prodItem?.powderArea,
                        margin: prodItem?.margin ?? editMarkDetails?.data[0]?.margin ?? "",
                        actuatorModel: prodItem?.actuatorModel || "",
                        actuatorListPrice: prodItem?.actuatorListPrice || 0,
                        savedActuatorModel: savedActuatorModelText === "-" ? "" : savedActuatorModelText,
                        savedActuatorCost: savedActuatorCostText,

                    })
                    // CFSD: saved Unit Price (variant 14) has the row margin added.
                    // Remove the damper margin so the screen shows the price without margin.
                    const isCfsdEdit = isCfsdFamilyGroup(editMarkDetails?.data[0]?.groupId);
                    const getSavedNumber = (ids) =>
                        Number(
                            String(
                                prodItem?.productItemVariants?.find((v) => ids?.includes(v?.variantId))?.variantValue ?? ""
                            ).replace(/,/g, "")
                        ) || 0;
                    const savedDamperList = getSavedNumber(urls?.LIST_PRICE_CONSTANT);
                    const savedUnitWithMargin = getSavedNumber(urls?.UNIT_PRICE_CONSTANT);
                    const savedRowMargin = Number(
                        prodItem?.margin ?? editMarkDetails?.data[0]?.margin ?? 0
                    ) || 0;

                    const savedDamperWithMargin = savedDamperList > 0
                        ? Number(calculateNetPrice({ listPrice: savedDamperList, margin: savedRowMargin }))
                        : 0;
                    const cfsdUnitWithoutMargin =
                        isCfsdEdit && savedDamperList > 0 && savedUnitWithMargin > 0
                            ? (Math.round(((savedUnitWithMargin - (savedDamperWithMargin - savedDamperList)) + Number.EPSILON) * 100) / 100).toFixed(2)
                            : null;

                    const savedRowQty = getSavedNumber(urls?.QUANTITY_CONSTANT) || 1;
                    const savedUnitBase = getSavedNumber(urls?.UNIT_PRICE_CONSTANT);
                    const totalWithMargin =
                        !isCfsdEdit && savedUnitBase > 0
                            ? Number(calculateNetPrice({
                                listPrice: savedUnitBase * savedRowQty,
                                margin: savedRowMargin,
                            })).toFixed(2)
                            : null;

                    prodItem?.productItemVariants?.map((varItem, varIndex) => {
                        const filterVar = productVariant?.find(
                            (pvar) => String(pvar?.variantId) === String(varItem?.variantId)
                        );
                        const savedVariantColumn = savedVariantColumns.find(
                            (variant) => String(variant?.variantId) === String(varItem?.variantId)
                        );
                        const resolvedVariant = filterVar || savedVariantColumn;
                        if (resolvedVariant) {
                            const isActuatorModelVariant = urls?.ACTUATOR_MODEL_CONSTANT?.includes(varItem?.variantId);
                            const hasValidArrayValue =
                                varItem?.variantValue !== null &&
                                varItem?.variantValue !== undefined &&
                                varItem?.variantValue !== "" &&
                                varItem?.variantValue !== "-";
                            const seededValue = isActuatorModelVariant
                                ? (hasValidArrayValue ? varItem.variantValue : (prodItem?.actuatorModel || ""))
                                : (cfsdUnitWithoutMargin !== null && urls?.UNIT_PRICE_CONSTANT?.includes(varItem?.variantId))
                                    ? cfsdUnitWithoutMargin
                                    : (totalWithMargin !== null && urls?.NET_UNIT_PRICE_CONSTANT?.includes(varItem?.variantId))
                                        ? totalWithMargin
                                        : varItem?.variantValue;

                            productVariantsArray.push({
                                sid: (prodIndex * 1) + 1,
                                id: varItem.id,
                                quoteProductItemId: varItem.quoteProductItemId,
                                variantId: varItem?.variantId,
                                variantValue: seededValue,
                                inputByUser: resolvedVariant.inputByUser,
                            })
                        }
                    })
                })

                if (productVariantsArray?.length > 0) {
                    setvariantsarray(productVariantsArray)
                }
                setProductConfigVariant(savedVariantColumns);
                setAddProduct(addProductArray);
                let specificationarr = []
                editMarkDetails?.data[0]?.productSpecifications?.map((spec, specIndex) => {

                    if ((spec.specificationValueId * 1) > 0) {
                        let specValueId = attributeDetails?.find(item =>
                            item?.productId === editMarkDetails?.data[0]?.productId
                            && item?.attributeId === spec.specificationId
                            && item?.valueItemId === spec.specificationValueId)

                        let specObj = {
                            attributeId: specValueId?.attributeId,
                            attributeName: specValueId?.attributeName,
                            valueId: 0,
                            valueName: specValueId?.valueName,
                            groupName: specValueId?.groupName,
                            productGroupId: editMarkDetails?.data[0]?.groupId,
                            productSubGroupId: editMarkDetails?.data[0]?.productSubGroupId,
                            specialValue: spec?.specialValue,
                        }
                        specificationarr.push(specObj)
                    } else {
                        if (CheckNull(spec?.specialValue) !== "") {
                            let specValueId = attributeDetails?.find(item =>
                                item?.productId === editMarkDetails?.data[0]?.productId
                                && item?.attributeId === spec.specificationId)
                            let specObj = {
                                attributeId: specValueId?.attributeId,
                                attributeName: specValueId?.attributeName,
                                valueId: 0,
                                valueName: "",
                                groupName: specValueId?.groupName,
                                productGroupId: editMarkDetails?.data[0]?.groupId,
                                productSubGroupId: editMarkDetails?.data[0]?.productSubGroupId,
                                specialValue: spec?.specialValue,
                            }
                            specificationarr.push(specObj)
                        }
                    }

                })
                const filterConfig = HandleChangeConfigSetting(specificationarr)
                setConfigSetting(filterConfig);
                const editProductDetails = attributeDetails?.find(
                    item => item?.productId === editMarkDetails?.data[0]?.productId
                );
                if (editProductDetails) {
                    setProductConfig(prev => ({
                        ...prev,
                        productName: {
                            productId: editProductDetails.productId,
                            productName: editProductDetails.productName,
                            productGroupId: editProductDetails.productGroupId,
                            productSubGroupId: editProductDetails.productSubGroupId,
                            imageUrl: editProductDetails.imageUrl || "",
                        }
                    }));
                }

                setLastMatchedProductId(editProductDetails.productId);
                setEditConfig({ status: true, configSetting: filterConfig })
                pendingTokenRefreshRef.current = true;
                setHasUnsavedChanges(false);
                setShowUnsavedModal(false);
            }

            setIsEditMarkDetailsLoading(false);
        }

        if (ProductTab?.status !== "editmark" && attributeArray?.length > 0 && configSetting?.length <= 0 && (addProduct?.length === 0 || assemblyTabs.length > 0) && !productConfig?.productName?.productName) {
            let specificationarr = []
            const selectedConfigByAttribute = new Map(
                (ProductTab?.selectedConfiguration || []).map((item) => [
                    item?.attributeName,
                    item?.valueName,
                ])
            );
            const selectedSpecialByAttribute = new Map(
                (ProductTab?.selectedConfiguration || [])
                    .filter((item) => item?.specialValue)
                    .map((item) => [item?.attributeName, item?.specialValue])
            );
            const selectedProductId = ProductTab?.id?.id || attributeDetails[0]?.productId;
            attributeArray?.map((filterAttr) => {
                const selectedValueName = selectedConfigByAttribute.get(filterAttr.attributeName);
                const selectedProduct = selectedValueName
                    ? attributeDetails?.find(item =>
                        item?.attributeId === filterAttr.attributeId &&
                        item?.productId === selectedProductId &&
                        item?.valueName === selectedValueName
                    )
                    : null;
                const defaultProduct = selectedProduct || getDefaultAttributeValue(
                    filterAttr.attributeId,
                    selectedProductId
                );
                if (defaultProduct) {
                    specificationarr.push({
                        attributeId: filterAttr.attributeId,
                        attributeName: filterAttr.attributeName,
                        valueId: 0,
                        valueName: defaultProduct?.valueName,
                        groupName: filterAttr?.groupName,
                        productGroupId: defaultProduct?.productGroupId,
                        productSubGroupId: defaultProduct?.productSubGroupId,
                        ...(selectedProduct && selectedSpecialByAttribute.has(filterAttr.attributeName)
                            ? { specialValue: selectedSpecialByAttribute.get(filterAttr.attributeName) }
                            : {}),
                    })
                }
            })
            const filtered = HandleChangeConfigSetting(specificationarr);
            setConfigSetting(filtered);
            setEditConfig({ status: true, configSetting: filtered })
            setHasUnsavedChanges(false);
            setShowUnsavedModal(false);
        }
    }, [editMarkDetails, productVariant, attributeDetails, attributeArray])

    const HandleChangeConfigSetting = (specificationarr) => {
        if (!specificationarr || specificationarr.length === 0) return [];

        const hasBankOfBlateNoValue = specificationarr.some(
            (item) => item.attributeId === urls.BANK_OF_BLATE && item.valueName === urls.BANK_OF_BLATE_NO_VALUE
        );

        const hasPlenumNoneValue = specificationarr.some(
            (item) => item.attributeId === urls.PLENUM && item.valueName === urls.PLENUM_NONE_VALUE
        );

        const hasPlenumWithoutInsulation = specificationarr.some(
            (item) => item.attributeId === urls.PLENUM && item.valueName === urls.PLENUM_WITHOUT_INSULATION_VALUE
        );

        return specificationarr.filter(
            (item) =>
                !(
                    (hasBankOfBlateNoValue && urls.BANK_OFF_BLATE_ARRAY.includes(item.attributeId)) ||
                    (hasPlenumNoneValue && urls.PLENUM_ATTRIBUTES_WITH_ARRAY.includes(item.attributeId)) ||
                    (hasPlenumWithoutInsulation && urls.PLENUM_ATTRIBUTES_WITHOUT_NOTALLOWEDARRAY.includes(item.attributeId))
                )
        );
    };

    const getDefaultAttributeValue = (attributeId, productId) => {
        const candidateValues = (attributeDetails ?? [])
            .filter(
                (item) =>
                    item?.attributeId === attributeId &&
                    item?.productId === productId &&
                    item?.valueName
            )
            .sort((a, b) => (a?.sequenceValue || 0) - (b?.sequenceValue || 0));

        if (!candidateValues.length) return null;

        return (
            candidateValues.find((item) => Number(item?.sequenceValue) === 1) ??
            candidateValues[0]
        );
    };


    useEffect(() => {
        const matchedId = productConfig?.productName?.productId;

        if (!matchedId) return;
        if (editMarkDetails?.data?.length) return;

        const isFirstMatch = lastMatchedProductId === null;
        const productChanged = !isFirstMatch && matchedId !== lastMatchedProductId;
        if ((isFirstMatch && addProduct?.length === 0 && productVariant?.length > 0 && configSetting?.length > 0) || productChanged) {
            setAddProduct([]);
            setvariantsarray([]);
            setLastMatchedProductId(matchedId);
            AddProduct(false);
            setHasUnsavedChanges(false);
            setShowUnsavedModal(false);
        }
    }, [productConfig?.productName?.productId, productVariant, configSetting])


    useEffect(() => {
        if (productSubmit?.markSubmit === true) {

            removeBlankRowsForSave();
            if (ValidConfiguration() === "error") return;
            if (ValidEntry() === "error") return;
            if (CheckNull(values?.id) !== "") {
                const quotationPayload = synchroniseQuotationColorCharges({
                    ...values,
                    modifiedBy: localStorage.getItem("authidentity"),
                });
                UpdateEntry(
                    "quotation",
                    quotationPayload,
                    setCreateMsg,
                    "success",
                    "Failed to update quotation"
                );
            }
            else {
                CreateEntry(
                    "quotation",
                    synchroniseQuotationColorCharges(values),
                    setCreateMsg,
                    "success",
                    "Failed to create quotation"
                );
            }
        }
    }, [productSubmit])

    useEffect(() => {
        if (CheckNull(createMsg?.message) == "success") {
            CreateMark();
        }
    }, [createMsg])

    const CreateMark = () => {
        if (isPriceRefreshPendingRef.current || pendingPriceRequestsRef.current > 0) {
            setErrorMessage("Price is updating, please wait a moment and click Save again.");
            setProductSubmit({ markRedirect: false, markSubmit: false });
            return;
        }
        removeBlankRowsForSave();
        if (ValidConfiguration() === "error") return;
        if (ValidEntry() === "error") return;
        // removeBlankRowsForSave updates React state asynchronously. Use the
        // filtered rows immediately so the trailing UI row is never submitted.
        const rowsToSave = addProduct.filter((row) => !isBlankSizeRow(
            row,
            variantsArray.filter((variant) => variant?.sid === row?.sid)
        ));
        let submitdata = [];
        if (productSubmit?.markSubmit === true) {
            let QuantityValue = 0
            let listPrice = 0
            let netPrice = 0
            const configuredPriceVariants = productVariant?.length > 0
                ? productVariant
                : productConfigVariant;
            const hasConfiguredListPrice = configuredPriceVariants?.some((variant) =>
                urls?.LIST_PRICE_CONSTANT?.includes(variant?.variantId)
            );
            const isManualPriceProduct =
                assemblyTabs.length > 0 ||
                !hasListPriceRoute(productConfig?.productName) || !hasConfiguredListPrice;

            const convertPriceForApi = (value) => {
                if (value === "" || value === null || value === undefined) return value;
                const parsed = Number(String(value).replace(/,/g, ""));
                return Number.isFinite(parsed) ? parsed : value;
            };

            rowsToSave.map((productitem, productindex) => {
                let list = variantsArray.filter(function (data) {
                    return data.sid === productitem?.sid
                })
                const rowUnitPrice = list.find((variant) =>
                    urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
                )?.variantValue;

                const isCfsdMark = isCfsdFamilyGroup(productConfig?.productName?.productGroupId);
                const rowTotalWithMargin =
                    Number(list.find((v) => urls?.NET_UNIT_PRICE_CONSTANT?.includes(v?.variantId))?.variantValue) || 0;
                const rowQty =
                    Number(list.find((v) => urls?.QUANTITY_CONSTANT?.includes(v?.variantId))?.variantValue) || 0;

                // CFSD: Total / Qty
                const unitWithMargin =
                    isCfsdMark && rowTotalWithMargin > 0 && rowQty > 0
                        ? Math.round(((rowTotalWithMargin / rowQty) + Number.EPSILON) * 100) / 100
                        : null;

                const variantslist = list?.map((element, index) => {
                    if (urls.QUANTITY_CONSTANT.includes(element?.variantId)) {
                        QuantityValue = (QuantityValue * 1) + (element?.variantValue * 1)
                    }
                    if (urls.NET_LIST_PRICE_CONSTANT.includes(element?.variantId)) listPrice = (listPrice * 1) + (element?.variantValue * 1)
                    if (urls.NET_UNIT_PRICE_CONSTANT.includes(element?.variantId)) netPrice = (netPrice * 1) + (element?.variantValue * 1)
                    let variantsdata = {
                        id: element.id,
                        quoteProductItemId: element.quoteProductItemId,
                        variantId: element.variantId,
                        // Imported price rows do not have a separate List
                        // Price response. Populate variant 12 from Unit Price
                        // so variants 12, 13 and 14 carry the same converted
                        // price in the mark API.
                        variantValue: urls?.PRICE_CONSTANT?.includes(element?.variantId)
                            ? convertPriceForApi(
                                (unitWithMargin !== null && urls?.UNIT_PRICE_CONSTANT?.includes(element?.variantId))
                                    ? unitWithMargin
                                    : (urls?.LIST_PRICE_CONSTANT?.includes(element?.variantId) &&
                                        (element?.variantValue === "" || element?.variantValue === null || element?.variantValue === undefined)
                                        ? rowUnitPrice
                                        : element?.variantValue)
                            )
                            : element.variantValue
                    }
                    return variantsdata;
                }
                );
                const filterVarList = variantsArray?.filter(
                    (pvar) => pvar.variantId === urls?.LIST_PRICE_CONSTANT[0]
                );
                const filterVarUnit = variantsArray?.filter(
                    (pvar) => pvar.variantId === urls?.UNIT_PRICE_CONSTANT[0]
                );

                // The API consumes prices on the item as well as in the
                // variant collection.  In particular, rows imported from
                // Excel do not go through the list-price API, so derive the
                // item values directly from the imported Unit Price and Qty.
                const getNumericVariantValue = (variantIds) => {
                    const value = list.find((variant) =>
                        variantIds?.includes(variant?.variantId)
                    )?.variantValue;
                    const parsed = Number(String(value ?? "").replace(/,/g, ""));
                    return Number.isFinite(parsed) ? parsed : 0;
                };
                const itemQuantity = getNumericVariantValue(urls?.QUANTITY_CONSTANT);
                const baseUnitPrice = getNumericVariantValue(urls?.UNIT_PRICE_CONSTANT);
                const savedTotalPrice = getNumericVariantValue(urls?.NET_UNIT_PRICE_CONSTANT);
                const itemUnitPrice = convertPriceForApi(baseUnitPrice);
                // Excel/manual-priced lines store the raw Unit Price × Qty in
                // variant 15. Rebuild the total from the row margin so the
                // saved line, its Total Price and quotation grand total agree.
                const calculatedManualTotal = calculateNetPrice({
                    listPrice: baseUnitPrice * itemQuantity,
                    margin: productitem?.margin ?? 0,
                });
                const itemTotalPrice = convertPriceForApi(
                    isManualPriceProduct
                        ? calculatedManualTotal
                        : (savedTotalPrice || (baseUnitPrice * itemQuantity))
                );

                const tokenParts = productitem?.costingToken?.split(".") || [];

                const tokenPayloads = tokenParts.reduce((payloads, tokenPart) => {
                    try {
                        const payload = JSON.parse(
                            atob(tokenPart.replace(/-/g, "+").replace(/_/g, "/"))
                        );
                        payloads.push(payload);
                    } catch (e) { }
                    return payloads;
                }, []);
                const hasPowderArea = (payload) =>
                    Object.prototype.hasOwnProperty.call(payload || {}, "powderAreaPerUnit") ||
                    Object.prototype.hasOwnProperty.call(payload || {}, "powderArea");
                const tokenPayload = tokenPayloads.find(hasPowderArea) || tokenPayloads[0] || null;
                const hasSignedPowderArea = hasPowderArea(tokenPayload);
                const signedPowderArea =
                    tokenPayload?.powderAreaPerUnit ?? tokenPayload?.powderArea;

                let params = {
                    id: productitem?.id,
                    quoteProductId: productitem?.quoteProductId,
                    notes: productitem?.notes,
                    specialDesignRequest: productitem?.specialDesignRequest,
                    sdrCharges: productitem?.sdrCharges,
                    costingToken: productitem?.costingToken,
                    ...(hasSignedPowderArea
                        ? { powderAreaPerUnit: signedPowderArea }
                        : {}),
                    margin: (productitem?.margin && !isNaN(Number(productitem.margin))) ? Number(productitem.margin) : 0,
                    unitPrice: itemUnitPrice,
                    totalPrice: itemTotalPrice,
                    productItemVariants: variantslist
                }
                submitdata.push(params)
            })


            // Build mark totals from the converted item amounts so they match
            // the values submitted in productItems.
            listPrice = submitdata.reduce(
                (sum, item) => sum + (Number(item?.unitPrice) || 0),
                0
            );
            netPrice = submitdata.reduce(
                (sum, item) => sum + (Number(item?.totalPrice) || 0),
                0
            );


            const normalize = (v) => (v ?? "").toString().trim().toLowerCase();
            const configToSave =
                (configSetting && configSetting.length > 0)
                    ? configSetting
                    : (productConfig?.configuration ?? []);

            const isEditSave = editMarkDetails?.data?.length > 0;
            const savedSpecs = editMarkDetails?.data?.[0]?.productSpecifications || [];

            let configItem = []
            configToSave?.forEach((config) => {
                // attributeId illaadha broken entry-a skip pannum
                if (config?.attributeId === undefined || config?.attributeId === null) return;

                const specValueId = attributeDetails?.find(item =>
                    item?.productId === productConfig?.productName?.productId
                    && item?.attributeId === config?.attributeId
                    && normalize(item?.valueName) === normalize(config?.valueName)
                )

                // Edit mode: DB-la save aana spec row (50832...) match pannum
                const savedSpec = isEditSave
                    ? savedSpecs.find((s) => Number(s?.specificationId) === Number(config?.attributeId))
                    : null;

                configItem.push({
                    id: isEditSave ? (savedSpec?.id || 0) : (specValueId?.id || 0),
                    quoteProductId: isEditSave ? (editMarkDetails?.data?.[0]?.id || 0) : 0,
                    specificationId: config?.attributeId,
                    specificationValueId: specValueId?.valueItemId || savedSpec?.specificationValueId || 0,
                    SpecialValue: config?.attributeId === urls?.RAL_COLOUR
                        ? normaliseColorCode(config?.specialValue)
                        : config?.specialValue || "",
                    SpecialCharges: 0
                })
            })

            const faiProduct = assemblyTabs.length > 0
                ? (ProductTab?.assemblyProduct ?? faiFromApi)
                : null;

            if (assemblyTabs.length > 0 && !faiProduct?.id && !isEditSave) {
                setErrorMessage("FAI product not loaded, please try again.");
                setProductSubmit({ markRedirect: false, markSubmit: false });
                return;
            }

            const saveProductId = (faiProduct?.id && !isEditSave)
                ? faiProduct.id
                : productConfig?.productName?.productId;
          
            if (faiProduct?.id && !isEditSave) {
                const faiSpecs = (faiProduct.productSpecGroupMaps ?? [])
                    .flatMap((group) => group.productSpecMaps ?? []);
                const selectedByName = new Map();
                (ProductTab?.allSelectedConfiguration ?? ProductTab?.selectedConfiguration ?? [])
                    .forEach((c) => selectedByName.set(normalize(c?.attributeName), c));
                (configToSave ?? [])
                    .forEach((c) => selectedByName.set(normalize(c?.attributeName), c));

                configItem = faiSpecs.flatMap((spec) => {
                    const selected = selectedByName.get(normalize(spec?.attributeName));
                    if (!selected) return [];
                    const value = (spec.productSpecValueMaps ?? []).find(
                        (v) => normalize(v?.valueName) === normalize(selected?.valueName)
                    );
                    return [{
                        id: 0,
                        quoteProductId: 0,
                        specificationId: spec.attributeId,
                        specificationValueId: value?.id || 0,
                        SpecialValue: spec.attributeId === urls?.RAL_COLOUR
                            ? normaliseColorCode(selected?.specialValue)
                            : spec.isSelectionParameter
                                ? (ProductTab?.assemblySelection?.models?.[spec.attributeName] || "")
                                : selected?.specialValue || "",
                        SpecialCharges: 0
                    }];
                });
            }

            if (editMarkDetails?.data?.length > 0) {
                if (submitdata?.length > 0) {
                    pendingColorChargeTargetRef.current = normaliseColorCode(
                        configItem.find(
                            (specification) => Number(specification?.specificationId) === Number(urls?.RAL_COLOUR)
                        )?.SpecialValue
                    );
                    let variantsubmitdata = {
                        id: editMarkDetails?.data[0]?.id,
                        quotationId: editMarkDetails?.data[0]?.quotationId,
                        markName: editMarkDetails?.data[0]?.markName,
                        productId: productConfig?.productName?.productId,
                        quantity: QuantityValue,

                        listPrice: netPrice,

                        margin: editMarkDetails?.data?.[0]?.margin ?? 0,
                        salesMargin: 0,
                        sellingPrice: netPrice,
                        totalDdp: netPrice,
                        productItems: submitdata,
                        productSpecifications: configItem
                    }
                    UpdateEntry("updateproductmark", variantsubmitdata, setMarkCreateMessage, "Product updated successfully", "Failed to update product")
                }

            } else {
                if (submitdata?.length > 0) {
                    let variantsubmitdata = {
                        id: 0,
                        quotationId: CheckNull(values?.id) !== "" ? values?.id : createMsg?.successData?.id,
                        markName: "mark" + ((values?.products?.length * 1) + 1),
                        productId: saveProductId,
                        quantity: QuantityValue,
                        // For Excel/manual-priced rows there is no List Price
                        // variant, so use the calculated line total instead.
                        // listPrice: listPrice || netPrice,
                        listPrice: netPrice,
                        // The first save establishes the legacy parent value;
                        // subsequent edits preserve it rather than replacing
                        // it with the first item's margin.
                        margin: submitdata[0]?.margin ?? 0,
                        salesMargin: 0,
                        sellingPrice: netPrice,
                        totalDdp: netPrice,
                        productItems: submitdata,
                        productSpecifications: configItem,
                    }
                    CreateEntry("productmark", variantsubmitdata, setMarkCreateMessage, "Product submitted successfully", "Failed to submit product")
                }
            }
        }
    }

    const openSaveSuccessModal = () => {
        if (showUnsavedModal) {
            setPendingSuccessModal(true);
            setShowUnsavedModal(false);
            return;
        }

        setSuccessShow(true);
    };

    const handleUnsavedModalExited = () => {
        if (pendingSuccessModal) {
            setPendingSuccessModal(false);
            setSuccessShow(true);
        }
    };

    useEffect(() => {
        if (markCreateMessage?.message?.includes("successfully") === true) {
            GetOne("quotation", { id: markCreateMessage?.successData?.quotationId }, setQuoteMsg)
            openSaveSuccessModal();
            setCreateMsg(null);
            markWorkspaceSnapshot();
            setHasUnsavedChanges(false);
        } else if (markCreateMessage?.message) {
            // Save failed: show the API error and re-enable Save.
            setErrorMessage(markCreateMessage.message);
            setProductSubmit({ markRedirect: false, markSubmit: false });
            setMarkCreateMessage(null);
        }
    }, [markCreateMessage])

    useEffect(() => {
        if (quoteMsg?.message === "success") {
            const configuredTargetColor = normaliseColorCode(
                configSetting?.find(
                    (specification) => Number(specification?.attributeId) === Number(urls?.RAL_COLOUR)
                )?.specialValue
            );
            const targetColor = pendingColorChargeTargetRef.current || configuredTargetColor;
            const cleanedQuotation = synchroniseQuotationColorCharges(
                transferStaleColorCharge(quoteMsg.data, targetColor)
            );
            const originalCharges = Array.isArray(quoteMsg.data?.colorCharges)
                ? quoteMsg.data.colorCharges
                : [];
            const cleanedCharges = Array.isArray(cleanedQuotation?.colorCharges)
                ? cleanedQuotation.colorCharges
                : [];

            setValues((previous) => synchroniseQuotationColorCharges({
                ...cleanedQuotation,
                // The mark endpoint can return an empty list while the
                // quotation still has approved colour data locally.
                colorAreaSummary: Array.isArray(quoteMsg.data?.colorAreaSummary) &&
                    quoteMsg.data.colorAreaSummary.length === 0 &&
                    Array.isArray(previous?.colorAreaSummary) && previous.colorAreaSummary.length > 0 &&
                    Array.isArray(quoteMsg.data?.products) && quoteMsg.data.products.length > 0
                    ? previous.colorAreaSummary
                    : quoteMsg.data?.colorAreaSummary,
                colorCharges: Array.isArray(quoteMsg.data?.colorCharges) &&
                    quoteMsg.data.colorCharges.length === 0 &&
                    Array.isArray(previous?.colorCharges) && previous.colorCharges.length > 0
                    ? previous.colorCharges
                    : cleanedQuotation?.colorCharges,
            }));

            // Persist the merged target row and remove the obsolete colour.
            if (JSON.stringify(originalCharges) !== JSON.stringify(cleanedCharges)) {
                const signature = `${quoteMsg.data?.id}:${JSON.stringify(cleanedCharges)}`;
                if (persistedColorChargeSignatureRef.current !== signature) {
                    persistedColorChargeSignatureRef.current = signature;
                    UpdateEntry(
                        "quotation",
                        {
                            ...cleanedQuotation,
                            modifiedBy: localStorage.getItem("authidentity"),
                        },
                        setQuoteDetails,
                        "Special color charges synchronized",
                        "Failed to synchronize special color charges"
                    );
                }
            }
            pendingColorChargeTargetRef.current = "";
        }

    }, [quoteMsg])

    useEffect(() => {
        if (CheckNull(quoteDetails?.message) == "success") {
            openSaveSuccessModal();
            setCreateMsg(null);
        }
    }, [quoteDetails])

    const BackTab = () => {
        if (hasUnsavedChanges) {
            setShowUnsavedModal(true);
            setPendingNavigation(true);
            return;
        }

        proceedNavigation();
    };

    const proceedNavigation = () => {
        if (ProductTab?.status === "editmark") {
            setProductSubmit({ markRedirect: false, markSubmit: false });
            setProductTab({ tab: "ProductList", id: 0 });
        } else {
            setProductSubmit({ markRedirect: false, markSubmit: false });
            setProductTab({
                tab: ProductTab?.redirectTab,
                productSubCategoryId: ProductTab?.productSubCategoryId,
                subCategoryId: ProductTab?.subCategoryId,
                title: ProductTab?.title,
                assemblySelection: ProductTab?.assemblySelection,
            });
        }
    };

    const handleUnsavedConfirm = () => {
        SaveProductMarkAdd();
        setHasUnsavedChanges(false);
        setShowUnsavedModal(false);
        if (pendingNavigation) {
            proceedNavigation();
        }
    };

    useEffect(() => {
        if (errorMessage) {
            setShowUnsavedModal(false);
            setPendingNavigation(false);
        }
    }, [errorMessage]);

    const CopyNewEntry = (copyItem) => {
        if (ValidConfiguration() === "error") return;
        if (ValidEntry() === "error") return;

        const list = [...addProduct];
        const filterProduct = list.filter(x => x.sid === copyItem.sid);
        const filterVarList = variantsArray?.filter(
            (pvar) => pvar.variantId === urls?.LIST_PRICE_CONSTANT[0]
        );
        const filterVarUnit = variantsArray?.filter(
            (pvar) => pvar.variantId === urls?.UNIT_PRICE_CONSTANT[0]
        );
        let uniqueNumber = Date.now() + Math.floor(Math.random() * 1000);
        setAddProduct(prev => [...prev, {
            sid: uniqueNumber,
            id: 0,
            quoteProductId: 0,
            notes: filterProduct[0].notes,
            specialDesignRequest: filterProduct[0]?.specialDesignRequest,
            sdrCharges: filterProduct[0]?.sdrCharges,
            costingToken: filterProduct[0]?.costingToken,
            margin: filterProduct[0]?.margin || "",
            actuatorModel: filterProduct[0]?.actuatorModel,
            actuatorOverride: filterProduct[0]?.actuatorOverride,
            actuatorOverrideCost: filterProduct[0]?.actuatorOverrideCost
        }])

        const filterVariants = variantsArray.filter(item => item.sid === copyItem.sid)
        if (filterVariants) {
            if (filterVariants?.length > 0) {
                let productVariantsArray = []
                filterVariants?.map((item, index) => {
                    productVariantsArray.push({
                        sid: uniqueNumber,
                        id: 0,
                        quoteProductItemId: 0,
                        variantId: item?.variantId,
                        variantValue: item?.variantValue,
                        inputByUser: item?.inputByUser
                    })
                })
                if (productVariantsArray?.length > 0) {
                    setvariantsarray(prev => [...prev, ...productVariantsArray])
                }
            }
        }
    }

    const InsertLineAbove = (item) => {
        insertSizeRowAt(item?.sid, "above");
    };

    const InsertLineBelow = (item) => {
        insertSizeRowAt(item?.sid, "below");
    };
    useEffect(() => {
        if (!invalidUnitPrice || invalidUnitPrice.length === 0) return;

        const stillInvalid = invalidUnitPrice.filter((entry) => {
            const unitPriceItem = variantsArray.find(
                (v) => v.sid === entry.sid && urls?.UNIT_PRICE_CONSTANT?.includes(v.variantId)
            );

            if (!unitPriceItem) return false;
            return CheckNull(unitPriceItem?.variantValue) <= 0;
        });

        if (stillInvalid.length !== invalidUnitPrice.length) {
            setInvalidUnitPrice(stillInvalid);
            if (stillInvalid.length === 0) {
                setErrorMessage((prev) =>
                    typeof prev === "string" && prev.includes("Unit net price value") ? null : prev
                );
            }
        }
    }, [variantsArray])

    useEffect(() => {
        if (!requiredFieldError || requiredFieldError.length === 0) return;

        const stillInvalid = requiredFieldError.filter((entry) => {
            const currentItem = variantsArray.find(
                (v) => v.sid === entry.sid && v.variantId === entry.variantId
            );
            if (!currentItem) return false;
            return CheckNull(currentItem?.variantValue) === "";
        });

        if (stillInvalid.length !== requiredFieldError.length) {
            setRequiredFieldError(stillInvalid);
            if (stillInvalid.length === 0) {
                setErrorMessage((prev) =>
                    typeof prev === "string" && prev.includes("Please fill the") ? null : prev
                );
            }
        }
    }, [variantsArray])

    useEffect(() => {
        setSelectedSizeRowSids((prev) => prev.filter((sid) => addProduct.some((item) => item?.sid === sid)));
    }, [addProduct]);

    useEffect(() => {
        pruneExcessBlankRows();
    }, [addProduct, variantsArray]);

    const creatingRowRef = useRef(false);
    const isPriceRefreshPendingRef = useRef(false);
    const marginRefreshTimerRef = useRef(null);
    const [marginRefreshSid, setMarginRefreshSid] = useState(null);
    const getVariantTemplate = () =>
        productConfigVariant?.length > 0 ? productConfigVariant : productVariant;

    const createBlankSizeRow = (sid) => ({
        sid,
        id: 0,
        quoteProductId: 0,
        notes: "",
        specialDesignRequest: "",
        sdrCharges: "",
        costingToken: "",
        margin: "",
    });

    const isOrientationVariant = (item) => {
        const name = (item?.variantName || "").trim().toLowerCase();
        return name === "orientation" || name.includes("orientation");
    };

    const createBlankSizeRowVariants = (sid) => {
        const isCfsd = isCfsdFamilyGroup(productConfig?.productName?.productGroupId);

        return (getVariantTemplate() || []).map((item) => ({
            sid,
            id: 0,
            quoteProductItemId: 0,
            variantId: item?.variantId,
            variantValue: isOrientationVariant(item)
                ? "Right"
                : (isCfsd && isCfsdTransitionVariant(item?.variantId))
                    ? CFSD_TRANSITION_DEFAULT
                    : "",
            inputByUser: item?.inputByUser,
        }));
    };

    const insertSizeRowAt = (targetSid, position = "below") => {
        const targetIndex = addProduct.findIndex((row) => String(row?.sid) === String(targetSid));
        if (targetIndex === -1) return null;

        const uniqueNumber = Date.now() + Math.floor(Math.random() * 1000);
        const insertIndex = position === "above" ? targetIndex : targetIndex + 1;
        const newRow = createBlankSizeRow(uniqueNumber);
        const newRowVariants = createBlankSizeRowVariants(uniqueNumber);

        setAddProduct((prev) => {
            const nextRows = [...prev];
            nextRows.splice(insertIndex, 0, newRow);
            return nextRows;
        });

        if (newRowVariants.length > 0) {
            setvariantsarray((prev) => [...prev, ...newRowVariants]);
        }

        setHasUnsavedChanges(true);
        return uniqueNumber;
    };

    const AddProduct = (markDirty = true) => {
        const uniqueNumber = Date.now() + Math.floor(Math.random() * 1000);
        setAddProduct((prev) => [...prev, createBlankSizeRow(uniqueNumber)]);

        const productVariantsArray = createBlankSizeRowVariants(uniqueNumber);
        if (productVariantsArray.length > 0) {
            setvariantsarray((prev) => [...prev, ...productVariantsArray]);
        }

        if (markDirty) {
            setHasUnsavedChanges(true);
        }
        return uniqueNumber;
    }
    const openedRowRef = useRef(new Set());
    // Manual Unit Price is available only to product types which do not have
    // a List Price variant/API. Do not infer this from a pricing token: a new
    // API-priced row has no token until its first list-price request finishes.
    const isManualPriceRow = (sid) => {
        const hasUnitPrice = variantsArray.some((variant) =>
            variant?.sid === sid && urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
        );
        const configuredVariants = productVariant?.length > 0
            ? productVariant
            : productConfigVariant;
        const hasConfiguredListPrice = configuredVariants?.some((variant) =>
            urls?.LIST_PRICE_CONSTANT?.includes(variant?.variantId)
        );
        return hasUnitPrice && (
            assemblyTabs.length > 0 ||
            !hasListPriceRoute(productConfig?.productName) || !hasConfiguredListPrice
        );
    };

    const HandleProductOnchange = (e, item) => {
        if (ValidConfiguration() === "error") return;

        // react-select (END_CAPS, WIDTH, HEIGHT) passes {label, value} directly.
        const rawValue = e?.target ? e.target.value : (e?.value ?? "");

        if (urls?.NUMBER_VALIDATION_ARRAY.includes(item?.variantId))
            if (/^\d*$/.test(rawValue) === false) return;

        if (urls?.QUANTITY_CONSTANT.includes(item?.variantId)) {
            if (rawValue !== "" && (parseInt(rawValue) === 0 || rawValue === "0")) return;
        }

        const index = variantsArray.findIndex(i => i.sid === item.sid && i.variantId === item.variantId)
        if (index === -1) return;
        // scheduleFocusNextRow(item.sid);

        const isWidthField = (item?.variantName || "").toLowerCase().includes("width");
        const previousValue = variantsArray[index]?.variantValue ?? "";
        const usesManualVariantPricing = isManualPriceRow(item?.sid);

        if (
            matchesVariantId(urls?.WIDTH, item?.variantId) ||
            matchesVariantId(urls?.HEIGHT, item?.variantId) ||
            matchesVariantId(urls?.CFSD_TRANSITION_TYPE, item?.variantId) ||
            matchesVariantId(urls?.ACTUATOR_LOCATION, item?.variantId)
        ) {
            clearRowActuatorOverride(item.sid);
        }

        if (previousValue === "" && rawValue !== "") {
            ensureNextRowExists(item.sid);
        }
        setvariantsarray(prev => {
            const newArr = [...prev];
            newArr[index] = {
                ...prev[index],
                variantValue: rawValue
            };

            // Width changed: check if sibling Height value is still valid, else clear it
            if (isWidthField && Array.isArray(combinations) && combinations.length > 0) {
                const heightConfig = productConfigVariant.find(
                    (v) => (v?.variantName || "").toLowerCase().includes("height")
                );
                if (heightConfig) {
                    const heightIndex = newArr.findIndex(
                        (v) => v.sid === item.sid && v.variantId === heightConfig.variantId
                    );
                    if (heightIndex !== -1) {
                        const currentHeightValue = newArr[heightIndex].variantValue;
                        if (currentHeightValue) {
                            const isStillValid = combinations.some((combo) => {
                                const widthMatch = combo?.options?.some(
                                    (opt) => opt?.variantId === item?.variantId && opt?.value === rawValue
                                );
                                const heightMatch = combo?.options?.some(
                                    (opt) => opt?.variantId === heightConfig.variantId && opt?.value === currentHeightValue
                                );
                                return widthMatch && heightMatch;
                            });
                            if (!isStillValid) {
                                newArr[heightIndex] = { ...newArr[heightIndex], variantValue: "" };
                            }
                        }
                    }
                }
            }

            const isCombinationFireSmoke = isCfsdFamilyGroup(productConfig?.productName?.productGroupId);

            // Combination Fire Smoke Damper: width / height change resets
            // Transition Type to "Select" (empty) and clears actuator values.
            if (
                isCombinationFireSmoke &&
                (
                    matchesVariantId(urls?.WIDTH, item?.variantId) ||
                    matchesVariantId(urls?.HEIGHT, item?.variantId)
                )
            ) {
                return newArr.map((variant) => {
                    if (variant?.sid !== item?.sid) return variant;
                    if (isCfsdTransitionVariant(variant?.variantId)) {
                        return { ...variant, variantValue: CFSD_TRANSITION_DEFAULT };
                    }
                    if (
                        matchesVariantId(urls?.CFSD_TRANSITION_TYPE, variant?.variantId) ||
                        matchesVariantId(urls?.ACTUATOR_LOCATION, variant?.variantId) ||
                        matchesVariantId(urls?.ACTUATOR_MODEL_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.ACTUATOR_QTY_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.ACTUATOR_COST_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.LIST_PRICE_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.NET_LIST_PRICE_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.UNIT_PRICE_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.NET_UNIT_PRICE_CONSTANT, variant?.variantId)
                    ) {
                        return { ...variant, variantValue: "" };
                    }
                    return variant;
                });
            }

            if (
                matchesVariantId(urls?.CFSD_TRANSITION_TYPE, item?.variantId) ||
                matchesVariantId(urls?.ACTUATOR_LOCATION, item?.variantId)
            ) {
                return newArr.map((variant) =>
                    variant?.sid === item?.sid && (
                        matchesVariantId(urls?.ACTUATOR_MODEL_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.ACTUATOR_QTY_CONSTANT, variant?.variantId) ||
                        matchesVariantId(urls?.ACTUATOR_COST_CONSTANT, variant?.variantId)
                    )
                        ? { ...variant, variantValue: "" }
                        : variant
                );
            }

            if (usesManualVariantPricing && (
                urls?.UNIT_PRICE_CONSTANT?.includes(item?.variantId) ||
                urls?.QUANTITY_CONSTANT?.includes(item?.variantId)
            )) {
                const unitPrice = Number(newArr.find((variant) =>
                    variant?.sid === item?.sid && urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
                )?.variantValue) || 0;
                const quantity = Number(newArr.find((variant) =>
                    variant?.sid === item?.sid && urls?.QUANTITY_CONSTANT?.includes(variant?.variantId)
                )?.variantValue) || 0;

                return newArr.map((variant) =>
                    variant?.sid === item?.sid && (
                        urls?.LIST_PRICE_CONSTANT?.includes(variant?.variantId) ||
                        urls?.NET_LIST_PRICE_CONSTANT?.includes(variant?.variantId)
                    )
                        ? { ...variant, variantValue: String(unitPrice) }
                        : variant?.sid === item?.sid && urls?.NET_UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
                            ? { ...variant, variantValue: String(unitPrice * quantity) }
                            : variant
                );
            }

            return newArr;
        })
        setHasUnsavedChanges(true);
        //Default Actuator Location
        if (e?.autoDefault && matchesVariantId(urls?.ACTUATOR_LOCATION, item?.variantId)) {
            const rowQty = String(parseInt(
                variantsArray.find(
                    (v) => v.sid === item.sid && matchesVariantId(urls?.QUANTITY_CONSTANT, v.variantId)
                )?.variantValue,
                10
            ) || 0);
            cfsdPricedQtyRef.current[item.sid] = rowQty;
            setActuatorRefreshSid(item.sid);
        }
    }

    const ValidEntry = () => {
        // Save removes trailing blank rows asynchronously. Exclude them from
        // validation immediately so the first Save click can continue to the
        // API request instead of requiring a second click.
        const blankRowSids = new Set(addProduct
            .filter((row) => isBlankSizeRow(
                row,
                variantsArray.filter((variant) => variant?.sid === row?.sid)
            ))
            .map((row) => row?.sid));
        const rowsToValidate = variantsArray.filter((item) => !blankRowSids.has(item?.sid));
        let nominalneckarr = rowsToValidate.filter((item) => (CheckNull(item.variantValue) === "") && urls.VALIDATION_VARIANT_ARRAY.includes(item.variantId))
        let unitPricearr = rowsToValidate.filter((item) => (CheckNull(item.variantValue) <= 0) && urls.UNIT_PRICE_CONSTANT.includes(item.variantId))
        const firstInvalidSid = nominalneckarr[0]?.sid;
        let variant = nominalneckarr
            .filter((item) => item.sid === firstInvalidSid)
            .map(item => item.variantId);
        let variantnamearr = productConfigVariant.filter((item) => variant.includes(item.variantId))
        let strvariantname = variantnamearr.map(item => item.variantName).join(",");

        if (nominalneckarr?.length > 0) {
            let index = (((addProduct?.findIndex(item => item?.sid === nominalneckarr[0].sid)) * 1) + 1)
            setErrorMessage("Line No :" + index + " , Please fill the " + strvariantname + " .");
            setRequiredFieldError(nominalneckarr);
            setProductSubmit({ markRedirect: false, markSubmit: false });
            return "error";
        } else if (unitPricearr?.length > 0) {
            setInvalidUnitPrice(unitPricearr)
            setRequiredFieldError([]);
            let index = (((addProduct?.findIndex(item => item?.sid === unitPricearr[0].sid)) * 1) + 1)
            setErrorMessage("Line No :" + index + " , Unit net price value must be greater than the zero .");
            setProductSubmit({ markRedirect: false, markSubmit: false });
            return "error";
        } else if (marginRangeError?.length > 0) {
            setErrorMessage(null);
            setProductSubmit({ markRedirect: false, markSubmit: false });
            return "error";
        }

        setErrorMessage(null);
        setInvalidUnitPrice(null);
        setRequiredFieldError([]);
    }
    const ValidConfiguration = () => {
        if (CheckNull(productConfig?.productName?.productName) === "") {
            setErrorMessage("There is no product match with your configuration");
            setProductSubmit({ markRedirect: false, markSubmit: false });
            return "error";
        }
        setErrorMessage(null);
    }

    const HandleOnChangenotes = (e, productitem, isClear) => {
        const index = addProduct.findIndex(i => i.sid === productitem.sid)
        if (index === -1) return;
        setAddProduct(prev => {
            const newArr = [...prev];
            newArr[index] = { ...prev[index], notes: isClear === true ? "" : e.target.value }
            return newArr
        })
    }

    const HandleOnChangeSDR = (e, productitem) => {
        const index = addProduct.findIndex(i => i.sid === productitem.sid)
        if (index === -1) return;
        setAddProduct(prev => {
            const newArr = [...prev];
            newArr[index] = { ...prev[index], [e.target.name]: e.target.value }
            return newArr
        })
    }

    const HandleOnChangeSDRClear = (e, productitem) => {
        const index = addProduct.findIndex(i => i.sid === productitem.sid)
        if (index === -1) return;
        setAddProduct(prev => {
            const newArr = [...prev];
            newArr[index] = { ...prev[index], specialDesignRequest: "", sdrCharges: "" }
            return newArr
        })
    }

    const deleteProductsBySid = (sids = []) => {
        const sidSet = new Set(sids);
        const nextRows = addProduct.filter((item) => !sidSet.has(item?.sid));

        setAddProduct(nextRows);
        setvariantsarray((prev) => prev.filter((item) => !sidSet.has(item?.sid)));
        setSelectedSizeRowSids((prev) => prev.filter((sid) => !sidSet.has(sid)));

        if (nextRows.length === 0) {
            setDeleteProduct(true);
        }
    };

    const DeleteProduct = (delItem) => {
        deleteProductsBySid([delItem?.sid]);
    }

    const ToggleSizeRowSelection = (sid) => {
        setSelectedSizeRowSids((prev) =>
            prev.includes(sid) ? prev.filter((item) => item !== sid) : [...prev, sid]
        );
    };

    const ToggleAllSizeRowSelection = () => {
        if (selectedSizeRowSids.length === addProduct.length) {
            setSelectedSizeRowSids([]);
            return;
        }

        setSelectedSizeRowSids(addProduct.map((item) => item?.sid));
    };

    const BulkDeleteSelectedRows = () => {
        if (!selectedSizeRowSids.length) return;
        deleteProductsBySid(selectedSizeRowSids);
    }

    useEffect(() => {
        if (deleteProduct === true && addProduct?.length === 0 && variantsArray?.length === 0) {
            AddProduct(false);
            setDeleteProduct(false);
        }
    }, [addProduct])

    const SuccessDoneClick = () => {
        setSuccessShow(false); setMarkCreateMessage(null);
        if (productSubmit?.markRedirect === true) {
            setProductSubmit({ markRedirect: false, markSubmit: false });
            setProductTab({ tab: "ProductList", id: 0, status: "updated" });
        }
    }

    const openedNextRowRef = useRef(new Set());
    const ensureNextRowExists = (currentSid) => {
        if (openedRowRef.current.has(currentSid)) return;

        const rows = addProductRef.current || [];
        const currentIndex = rows.findIndex(
            (r) => String(r.sid) === String(currentSid)
        );

        if (currentIndex === rows.length - 1) {
            AddProduct();
        }

        openedRowRef.current.add(currentSid);
    };
    const openNextRowOnce = (sid) => {
        if (openedNextRowRef.current.has(sid)) {
            return;
        }

        openedNextRowRef.current.add(sid);
        ensureNextRowExists(sid);
    };

    const HandleProductonfocusout = (item, productindex) => {
        // Tabbing through an untouched cell (notably the first Tag Ref cell)
        // must behave like native form navigation. Previously its blur opened
        // a new row and started price processing, remounting the grid before
        // the browser could focus the next field.
        const currentVariantValue = variantsArray.find(
            (variant) => variant?.sid === item?.sid && variant?.variantId === item?.variantId
        )?.variantValue;
        if (CheckNull(currentVariantValue) === "") {
            return;
        }

        if (urls?.QUANTITY_CONSTANT.includes(item?.variantId)) {
            const currentValue = variantsArray.find(
                (i) => i.sid === item.sid && i.variantId === item.variantId
            )?.variantValue;

            if (
                currentValue === "0" ||
                currentValue === "" ||
                parseInt(currentValue) === 0
            ) {
                const index = variantsArray.findIndex(
                    (i) => i.sid === item.sid && i.variantId === item.variantId
                );

                if (index !== -1) {
                    setvariantsarray((prev) => {
                        const newArr = [...prev];
                        newArr[index] = {
                            ...prev[index],
                            variantValue: "",
                        };
                        return newArr;
                    });
                }

                return;
            }
        }

        // Excel/manual variant-priced products deliberately have no list-price
        // API. Their totals are maintained in HandleProductOnchange, so do not
        // send them through the existing API pricing flow on blur.
        const usesManualVariantPricing = isManualPriceRow(item?.sid);
        if (usesManualVariantPricing) {
            openNextRowOnce(item.sid);
            return;
        }

        // Combination Fire Smoke Damper: call the price API only when
        // Actuator Location OR Quantity is changed (blur on any other field is ignored).
        const isCombinationFireSmoke = isCfsdFamilyGroup(productConfig?.productName?.productGroupId);
        if (isCombinationFireSmoke) {
            const isActuatorLocationField = matchesVariantId(urls?.ACTUATOR_LOCATION, item?.variantId);
            const isQuantityField = matchesVariantId(urls?.QUANTITY_CONSTANT, item?.variantId);

            const rowQty = String(parseInt(
                variantsArray.find(
                    (v) => v.sid === item.sid && matchesVariantId(urls?.QUANTITY_CONSTANT, v.variantId)
                )?.variantValue,
                10
            ) || 0);
            const actuatorLocationValue = variantsArray.find(
                (v) => v.sid === item.sid && matchesVariantId(urls?.ACTUATOR_LOCATION, v.variantId)
            )?.variantValue;


            if (isQuantityField) {
                // Actuator Location select pannala (price calc aagala) illa same qty ku already calculate aachu na call illa
                if (CheckNull(actuatorLocationValue) === "" || cfsdPricedQtyRef.current[item.sid] === rowQty) {
                    openNextRowOnce(item.sid);
                    return;
                }
            } else if (!isActuatorLocationField) {
                openNextRowOnce(item.sid);
                return;
            }

            cfsdPricedQtyRef.current[item.sid] = rowQty;
        }


        // Existing API-pricing logic.
        HandleProductOnChange(item, productindex);

        openNextRowOnce(item.sid);
    };
    const getRowEditableCells = (rowElement) =>
        Array.from(rowElement.querySelectorAll('[data-size-editable="true"]'));

    const focusFirstEditableInRow = (rowSid) => {
        const rowElement = document.querySelector(`tr[data-size-row-sid="${rowSid}"]`);
        if (!rowElement) return false;

        const editableCell = getRowEditableCells(rowElement)[0];
        if (!editableCell) return false;

        const input = editableCell.querySelector(
            'input:not([type="hidden"]):not([type="checkbox"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"], .size-select__control'
        );

        const target = input || editableCell;
        if (typeof target.focus !== "function") return false;

        target.focus({ preventScroll: true });
        if (typeof target.select === "function" && target.tagName === "INPUT") {
            target.select();
        }
        return true;
    };

    const scheduleFocusNextRow = (currentRowSid) => {
        if (pendingSizeFocusFrameRef.current) {
            cancelAnimationFrame(pendingSizeFocusFrameRef.current);
        }

        const request = {
            currentRowSid: String(currentRowSid),
            attempts: 0,
            createdRowSid: null,
        };

        pendingSizeFocusRef.current = request;

        const tick = () => {
            const pending = pendingSizeFocusRef.current;
            if (!pending || pending !== request) return;

            const rows = addProductRef.current || [];

            const currentIndex = rows.findIndex(
                row => String(row.sid) === pending.currentRowSid
            );

            const nextRowSid =
                currentIndex >= 0
                    ? rows[currentIndex + 1]?.sid
                    : null;

            if (
                currentIndex === rows.length - 1 &&
                !nextRowSid &&
                !pending.createdRowSid
            ) {
                pending.createdRowSid = AddProduct();
            }

            const targetRowSid = nextRowSid || pending.createdRowSid;

            if (targetRowSid && focusFirstEditableInRow(targetRowSid)) {
                pendingSizeFocusRef.current = null;
                pendingSizeFocusFrameRef.current = null;
                return;
            }

            if (pending.attempts < 30) {
                pending.attempts++;
                pendingSizeFocusFrameRef.current =
                    requestAnimationFrame(tick);
                return;
            }

            pendingSizeFocusRef.current = null;
            pendingSizeFocusFrameRef.current = null;
        };

        pendingSizeFocusFrameRef.current =
            requestAnimationFrame(tick);
    };

    const handleKeyDown = (event) => {
        setLastKey(event.key);

        if (event.key !== "Tab" || event.shiftKey) return;

        const currentRow = event.currentTarget?.closest("tr[data-size-row-sid]");
        if (!currentRow) return;

        const currentCell = event.currentTarget?.closest('[data-size-editable="true"]');
        if (!currentCell) return;

        const editableCells = getRowEditableCells(currentRow);
        const lastEditableCell = editableCells[editableCells.length - 1];
        if (currentCell !== lastEditableCell) return;

        event.preventDefault();
        scheduleFocusNextRow(currentRow.dataset.sizeRowSid);
    };

    const closeActionMenu = () => {
        setActionMenuOpen({});
        setSDROpen({});
    };

    const toggleActionMenu = (event, sid) => {
        if (actionMenuOpen?.show === "show" && actionMenuOpen?.showid === sid) {
            closeActionMenu();
            return;
        }

        const triggerRect = event.currentTarget.getBoundingClientRect();
        const gridScrollRect = event.currentTarget
            .closest(".product-size-grid-scroll")
            ?.getBoundingClientRect();
        const menuWidth = 130;
        const menuHeight = 110;
        const viewportPadding = 8;
        const menuGap = 4;
        const boundsTop = Math.max(viewportPadding, gridScrollRect?.top ?? viewportPadding);
        const boundsBottom = Math.min(
            window.innerHeight - viewportPadding,
            gridScrollRect?.bottom ?? window.innerHeight - viewportPadding
        );
        const boundsLeft = Math.max(viewportPadding, gridScrollRect?.left ?? viewportPadding);
        const boundsRight = Math.min(
            window.innerWidth - viewportPadding,
            gridScrollRect?.right ?? window.innerWidth - viewportPadding
        );

        const centeredTop = triggerRect.top + (triggerRect.height / 2) - (menuHeight / 2);
        const top = Math.min(
            boundsBottom - menuHeight,
            Math.max(boundsTop, centeredTop)
        );
        const left = Math.min(
            boundsRight - menuWidth,
            Math.max(boundsLeft, triggerRect.left - menuWidth - menuGap)
        );

        setActionMenuPosition({ top, left });
        setSDROpen({});
        setActionMenuOpen({
            show: "show",
            showid: sid
        });
    };

    useEffect(() => {
        if (actionMenuOpen?.show !== "show") return;
        const handleOutsideClick = (e) => {
            if (!e.target.closest(".action-cell") && !e.target.closest(".quote-action-menu")) {
                closeActionMenu();
            }
        };
        const handleViewportChange = () => closeActionMenu();
        document.addEventListener("click", handleOutsideClick);
        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);
        return () => {
            document.removeEventListener("click", handleOutsideClick);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [actionMenuOpen]);

    const RecalculatePrice = (e) => {
        setProductConfig({ ...productConfig, configuration: configSetting, productName: productName });
        setRecalculateLoading(true);
        setRecalculate(true)
        setRecalculateModalShow(false)
    }

    useEffect(() => {
        if (recalculate === true) {
            const apiCalls = addProduct.map((productItem, productIndex) =>
                Promise.resolve(HandleProductOnChange({ sid: productItem?.sid }, productIndex))
            );
            Promise.all(apiCalls)
                .then(() => {
                    setRecalculateLoading(false);
                    setRecalculate(false);
                })
                .catch((error) => {
                    console.error("Error processing products:", error);
                });
        }
    }, [recalculate, productConfig?.productName])

    const HandleCancelRecalculate = () => {
        setRecalculateModalShow(false)
        setConfigSetting(productConfig?.configuration)
    }

    const hasEnteredProductValues = () =>
        (variantsArray ?? []).some(
            (item) =>
                CheckNull(item?.variantValue) !== "" &&
                !urls?.LIST_PRICE_CONSTANT?.includes(item?.variantId) &&
                !urls?.NET_LIST_PRICE_CONSTANT?.includes(item?.variantId) &&
                !urls?.UNIT_PRICE_CONSTANT?.includes(item?.variantId) &&
                !urls?.NET_UNIT_PRICE_CONSTANT?.includes(item?.variantId)
        );

    const HandleConfigurationChange = () => {
        setHasUnsavedChanges(true);
    };

    useEffect(() => {
        const currentConfiguration = productConfig?.configuration ?? [];
        const currentSignature = JSON.stringify(
            [...currentConfiguration]
                .map((item) => ({
                    attributeId: item?.attributeId,
                    valueName: item?.valueName ?? "",
                    specialValue: item?.specialValue ?? "",
                }))
                .sort((a, b) => a.attributeId - b.attributeId)
        );

        if (!currentSignature || currentSignature === "[]") {
            lastConfigurationSignatureRef.current = currentSignature;
            return;
        }

        if (!lastConfigurationSignatureRef.current) {
            lastConfigurationSignatureRef.current = currentSignature;
            return;
        }

        if (lastConfigurationSignatureRef.current === currentSignature) {
            return;
        }

        lastConfigurationSignatureRef.current = currentSignature;
        if (ProductTab?.status === "editmark" && !hasUnsavedChanges) return;
        if (!productConfig?.productName?.productId) return;
        if (!addProduct?.length) return;
        if (!hasEnteredProductValues()) return;

        const apiCalls = addProduct.map((productItem, productIndex) =>
            Promise.resolve(HandleProductOnChange({ sid: productItem?.sid }, productIndex, false))
        );

        Promise.all(apiCalls).catch((error) => {
            console.error("Error recalculating product list price after configuration change:", error);
        });
    }, [productConfig?.configuration, productConfig?.productName?.productId, addProduct, variantsArray]);

    const totalQuantity = variantsArray
        ?.filter((v) => urls?.QUANTITY_CONSTANT?.includes(v?.variantId))
        ?.reduce((sum, v) => sum + (Number(v?.variantValue) || 0), 0) || 0;

    const grandTotalNet = addProduct.reduce((sum, product) => {
        const rowVariants = variantsArray.filter((variant) => variant?.sid === product?.sid);
        const unitPrice = Number(rowVariants.find((variant) =>
            urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
        )?.variantValue);
        const quantity = Number(rowVariants.find((variant) =>
            urls?.QUANTITY_CONSTANT?.includes(variant?.variantId)
        )?.variantValue);
        const savedTotal = Number(rowVariants.find((variant) =>
            urls?.NET_UNIT_PRICE_CONSTANT?.includes(variant?.variantId)
        )?.variantValue);

        const isManualRow = isManualPriceRow(product?.sid);
        const total = isManualRow && Number.isFinite(unitPrice) && Number.isFinite(quantity)
            ? calculateNetPrice({ listPrice: unitPrice, margin: product?.margin ?? 0 }) * quantity
            : savedTotal;

        return sum + (Number.isFinite(total) ? total : 0);
    }, 0);

    const grandTotalNetDisplay = grandTotalNet;
    const [editingId, setEditingId] = useState(null);
    const defaultTab = ProductTab?.status === "editmark" ? "sizes" : "configuration";
    const [activeTab, setActiveTab] = useState(defaultTab);
    const tableColumns = useMemo(() => productConfigVariant
        .filter((variant) => !isVariantHidden(variant, productConfig?.configuration))
        .map((variant) => ({ key: variant?.variantId, label: variant?.variantName || "Unnamed column", defaultVisible: true })), [productConfigVariant, productConfig?.configuration]);
    const defaultVisibleColumns = tableColumns.filter((column) => column.defaultVisible).map((column) => column.key);
    const tableColumnSignature = defaultVisibleColumns.join("|");

    useEffect(() => {
        if (!tableColumnSignature) return;

        const storedColumns = readStoredVisibleColumns(columnVisibilityStorageKey);
        const isMatchingWorkspace = storedColumns?.signature === tableColumnSignature;
        // Apply the user's saved column chooser selection for both new and
        // existing item marks. The grid still falls back to every available
        // column when this product has no saved visibility preference.
        const nextVisibleColumns = isMatchingWorkspace
            ? storedColumns.columns.filter((columnKey) => defaultVisibleColumns.includes(columnKey))
            : defaultVisibleColumns;
        const workspaceSignature = `${columnVisibilityStorageKey}:${tableColumnSignature}`;

        if (columnDefaultsSignatureRef.current !== workspaceSignature) {
            columnDefaultsSignatureRef.current = workspaceSignature;
            setVisibleColumns(nextVisibleColumns);
            setPendingColumns(nextVisibleColumns);
        }
    }, [tableColumnSignature, columnVisibilityStorageKey, defaultVisibleColumns]);


    useEffect(() => {
        if (!pendingTokenRefreshRef.current) return;
        if (ProductTab?.status !== "editmark") return;
        if (!productConfig?.productName?.productId) return;
        if (!configSetting?.length || !addProduct?.length || !variantsArray?.length) return;

        pendingTokenRefreshRef.current = false;
        addProduct.forEach((row, rowIndex) => {
            if (isManualPriceRow(row?.sid)) return;
            HandleProductOnChange({ sid: row?.sid, restoreSavedActuator: true }, rowIndex, false);
        });
    }, [productConfig?.productName?.productId, configSetting, addProduct, variantsArray]);

    useEffect(() => {
        if (marginRefreshSid === null) return;
        HandleProductOnChange({ sid: marginRefreshSid }, 0, false);
        isPriceRefreshPendingRef.current = false;
        setMarginRefreshSid(null);
    }, [marginRefreshSid]);

    useEffect(() => {
        if (actuatorRefreshSid === null) return;
        const rowIndex = addProduct.findIndex((row) => row?.sid === actuatorRefreshSid);
        HandleProductOnChange({ sid: actuatorRefreshSid }, rowIndex >= 0 ? rowIndex : 0, false);
        setActuatorRefreshSid(null);
    }, [actuatorRefreshSid]);

    useEffect(() => {
        if (actuatorOverrideRequest === null) return;
        const { sid, model } = actuatorOverrideRequest;
        const rowIndex = addProduct.findIndex((row) => row?.sid === sid);
        HandleProductOnChange(
            { sid, actuatorModelOverride: model },
            rowIndex >= 0 ? rowIndex : 0,
            false
        );
        setActuatorOverrideRequest(null);
    }, [actuatorOverrideRequest]);

    useEffect(() => {
        setActiveTab(defaultTab);
    }, [defaultTab]);


    useEffect(() => {
        return () => clearTimeout(marginRefreshTimerRef.current);
    }, []);

    const headerErrorMessage =
        errorMessage ||
        // (marginRangeError?.length > 0 ? marginRangeError[0]?.message : null) ||
        (invalidUnitPrice?.length > 0 ? "Unit price not available for this size. Please recheck size." : null);

    return (<>
        <div className="row product-details-list-row">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <ProductMarkWorkspaceHeader
                    activeTab={activeTab}
                    defaultTab={defaultTab}
                    onTabChange={setActiveTab}
                    onBack={BackTab}
                    canImport={
                        activeTab === "sizes" &&
                        (
                            CheckNull(productConfig?.productName?.productName) !== "" ||
                            ProductTab?.status === "editmark"
                        )
                    }
                    onImport={() => setModalShow(true)}
                    printNode={null}
                    onSave={() => {
                        writeStoredVisibleColumns(columnVisibilityStorageKey, {
                            signature: tableColumnSignature,
                            columns: visibleColumns,
                        });
                        SaveProductMarkAdd();
                    }}
                    isRevisionQuotation={isRevisionQuotation}
                    onSaveRevisionDraft={() => {
                        writeStoredVisibleColumns(columnVisibilityStorageKey, {
                            signature: tableColumnSignature,
                            columns: visibleColumns,
                        });
                        onSaveRevisionDraft?.();
                    }}
                    onCompleteRevision={onCompleteRevision}
                    revisionCompleting={revisionCompleting}
                    saving={productSubmit?.markSubmit}
                    selectedSizeCount={selectedSizeRowSids.length}
                    onBulkDeleteSelectedRows={BulkDeleteSelectedRows}
                    availableColumns={activeTab === "sizes" ? tableColumns : []}
                    visibleColumns={visibleColumns}
                    pendingColumns={pendingColumns}
                    onPendingColumnsChange={setPendingColumns}
                    onResetColumns={() => {
                        // Reset is immediately reflected in the grid, but is
                        // intentionally not persisted until the user saves.
                        setPendingColumns(defaultVisibleColumns);
                        setVisibleColumns(defaultVisibleColumns);
                    }}
                    onApplyColumns={() => {
                        const nextColumns = pendingColumns.filter((columnKey) =>
                            defaultVisibleColumns.includes(columnKey)
                        );
                        setVisibleColumns(nextColumns);
                    }}
                    selectedModel={selectedModel}
                    errorMessage={headerErrorMessage}
                />

                <div className="row">

                    <div className={activeTab === "sizes" ? "col-12 product-size-main-col" : "col-xxl-9 col-xl-9 col-lg-8 col-md-12 product-config-main-col"}>
                        <div className="tab-content" id="nav-tabContent">
                            <div className={`tab-pane fade ${activeTab === "configuration" ? "show active" : ""}`} id="nav-configuration" role="tabpanel" aria-labelledby="nav-configuration-tab">
                                <Configuration
                                    inline={true}
                                    setProductTab={setProductTab}
                                    ProductTab={ProductTab}
                                    setProductConfig={setProductConfig}
                                    productConfig={productConfig}
                                    attributes={attributes}
                                    setAttributes={setAttributes}
                                    configSetting={configSetting}
                                    setConfigSetting={setConfigSetting}
                                    setProductConfigVariant={setProductConfigVariant}
                                    productVariant={productVariant}
                                    setvariantsarray={setvariantsarray}
                                    variantsArray={variantsArray}
                                    addProduct={addProduct}
                                    attributeDetails={attributeDetails}
                                    setattributeDetails={setattributeDetails}
                                    setRecalculateModalShow={setRecalculateModalShow}
                                    setProductName={setProductName}
                                    productName={productName}
                                    editConfig={editConfig}
                                    setEditConfig={setEditConfig}
                                    attributeArray={attributeArray}
                                    setAttributeArray={setAttributeArray}
                                    attributeValueArray={attributeValueArray}
                                    setAttributeValueArray={setAttributeValueArray}
                                    groupArray={groupArray}
                                    setGroupArray={setGroupArray}
                                    recalculate={recalculate}
                                    onConfigurationChange={HandleConfigurationChange}
                                    headerExtra={assemblyTabsNode}
                                    groupFilter={configGroupFilter}
                                />
                            </div>
                            <div className={`tab-pane fade ${activeTab === "sizes" ? "show active" : ""}`} id="nav-sizes" role="tabpanel" aria-labelledby="nav-sizes-tab">
                                <ProductSizeGrid
                                    productConfigVariant={productConfigVariant}
                                    addProduct={addProduct}
                                    variantsArray={variantsArray}
                                    productConfig={productConfig}
                                    configuration={configSetting}
                                    values={values}
                                    currencyDetail={currencyDetail}
                                    companyBaseCurrency={companyBaseCurrency}
                                    manualPriceRowSids={addProduct
                                        .filter((product) => isManualPriceRow(product?.sid))
                                        .map((product) => product?.sid)}
                                    optionsMap={optionsMap}
                                    combinations={combinations}
                                    optionsLoading={optionsLoading}
                                    invalidUnitPrice={invalidUnitPrice}
                                    marginRangeError={marginRangeError}
                                    requiredFieldError={requiredFieldError}
                                    endcaplist={endcaplist}
                                    editingId={editingId}
                                    setEditingId={setEditingId}
                                    actionMenuOpen={actionMenuOpen}
                                    actionMenuPosition={actionMenuPosition}
                                    sdrOpen={sdrOpen}
                                    totalQuantity={totalQuantity}
                                    grandTotalNetDisplay={grandTotalNetDisplay}
                                    onProductChange={HandleProductOnchange}
                                    onProductBlur={HandleProductonfocusout}
                                    onKeyDown={handleKeyDown}
                                    onNotesChange={HandleOnChangenotes}
                                    onToggleActionMenu={toggleActionMenu}
                                    onCloseActionMenu={closeActionMenu}
                                    onInsertLineAbove={InsertLineAbove}
                                    onInsertLineBelow={InsertLineBelow}
                                    onCopy={CopyNewEntry}
                                    onDelete={DeleteProduct}
                                    selectedSizeRowSids={selectedSizeRowSids}
                                    onToggleSizeRowSelection={ToggleSizeRowSelection}
                                    onToggleAllSizeRowSelection={ToggleAllSizeRowSelection}
                                    onBulkDeleteSelectedRows={BulkDeleteSelectedRows}
                                    onToggleSdr={setSDROpen}
                                    onSdrChange={HandleOnChangeSDR}
                                    onSdrClear={HandleOnChangeSDRClear}
                                    onMarginChange={HandleOnChangeMargin}
                                    onActuatorOverride={HandleActuatorOverride}
                                    errorMessage={errorMessage}
                                    isLoading={isEditMarkDetailsLoading}
                                    visibleColumnKeys={visibleColumns}
                                />
                            </div>
                        </div>
                    </div>
                    {activeTab === "configuration" && (
                        <div className="col-xxl-3 col-xl-3 col-lg-4 col-md-12 product-config-preview-col">
                            <ProductPreviewPanel
                                configSetting={configSetting}
                                attributeDetails={attributeDetails}
                                productConfig={productConfig}
                                imageUrl={isAccessoriesTab ? activeAssemblyTab?.imageUrl : productConfig?.productName?.imageUrl}
                                modelNo={productConfig?.productName?.productCode}
                                productLabel={isAccessoriesTab ? (activeAssemblyTab?.title || "FAI") : productConfig?.productName?.productName}
                                productCategory={productConfig?.productName?.productCategory}
                                summaryItems={faiSummaryItems}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
        <PriceRecalculate modalShow={recalculateModalShow} setModalShow={setRecalculateModalShow} RecalculatePrice={RecalculatePrice} HandleCancelRecalculate={HandleCancelRecalculate} />
        <ProductImportFile
            modalShow={modalShow}
            setModalShow={setModalShow}
            productVariant={productVariant}
            setAddProduct={setAddProduct}
            addProduct={addProduct}
            productConfigVariant={productConfigVariant}
            variantsArray={variantsArray}
            setvariantsarray={setvariantsarray}
            HandleProductOnChange={HandleProductOnChange}
            setRecalculateLoading={setRecalculateLoading}
            productConfig={productConfig}
            endcaplist={endcaplist}
            optionsMap={optionsMap}
            combinations={combinations}
        />
        <SaveSuccessModel
            show={successshow}
            createMsg={
                isRevisionQuotation &&
                    values?.isRevisionDraft &&
                    markCreateMessage?.message?.includes("successfully")
                    ? { ...markCreateMessage, message: "Quotation draft saved successfully." }
                    : markCreateMessage
            }
            setShow={setSuccessShow}
            SuccessDoneClick={SuccessDoneClick}
        />
        <UnSaveModel
            show={showUnsavedModal}
            setShow={setShowUnsavedModal}
            onExited={handleUnsavedModalExited}
            createMsg={{ message: "You want to save this changes" }}
            title="Unsaved Changes"
            buttonText="Save & Continue"
            SuccessDoneClick={handleUnsavedConfirm}
            SaveProductMarkAdd={SaveProductMarkAdd}
        />
        {recalculateLoading && <Loader />}
    </>
    )
}
